/**
 * O painel do @DevOps (spec 2026-11-04-painel-do-devops, fatia 1): o andamento do runner
 * fora da conversa, aberto ao lado dela como o `PainelDoDba` ao lado do editor.
 *
 * Por STAGE, ancorado num pedido (D-4): o servidor resolve o stage do pedido âncora, e o
 * painel mostra quem ocupa o stage, a pausa, a fila, o diff, o plan, o andamento, a
 * conferência, os recentes e os itens da lista que a pessoa pediu.
 *
 * SEM AÇÃO (D-5, guardrail 1): nenhum botão além de recolher/expandir e "Ir ao cartão",
 * que só rola a conversa até a mensagem do cartão. A resposta não traz `pode_*` e o painel
 * não deduz nenhum: o `PausaDaFila` recebe `pode_retomar: false` (não desenha o Retomar),
 * e não há `DecisaoDoPedido`, Confirmar nem Aprovar aqui — toda decisão segue nos portões
 * de hoje (o cartão e o chamado). O andamento ao vivo é o `PainelDeExecucao`, pela rota
 * `/andamento` de hoje: nenhuma leitura nova de log.
 *
 * Fila opaca (D-9): o pedido que quem vê não lê aparece como "um pedido que você não
 * acompanha", só com a posição — sem número nem estado.
 */
import { useState, type ReactNode } from 'react'
import { Badge, Box, Button, Code, Group, Stack, Text } from '@mantine/core'
import {
  IconAlertTriangle, IconCheck, IconChevronDown, IconChevronRight, IconClock, IconMessageCircle,
  IconPlayerPlay,
} from '@tabler/icons-react'
import type {
  CorpoDoPainel, ItemDaLista, PainelDoStage, PausaDoPainel, PausaDoStage,
} from '@/api/terraformRunner'
import { useDiffDoPedido, usePainelDoStage } from '@/hooks/useTerraformChangeRequest'
import { ChecagensVerificadas, NaoVerificadas } from './ChecagensDoLaudo'
import { Dado } from './Dado'
import { LinhaDoTempoDoPedido } from './LinhaDoTempoDoPedido'
import { PainelDeExecucao, totalDoPlan } from './PainelDeExecucao'
import { PausaDaFila } from './PausaDaFila'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }
const OPACO = 'um pedido que você não acompanha'
/** Os estados em que a vez é de quem pediu, e a decisão está no cartão (D-2). */
const VEZ_DO_PEDINTE = ['aguardando_confirmacao', 'aguardando_pedinte']

function prazo(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

/** O rótulo do estado vem da linha do tempo (`linha_do_tempo.py`): a mesma fonte do
 * cartão e do painel do chamado. */
function rotuloDoEstado(pedido: CorpoDoPainel): string {
  return pedido.linha_do_tempo?.etapas?.find((e) => e.estado === pedido.estado)?.rotulo ?? pedido.estado
}

/** O que o `PausaDaFila` lê, montado da pausa do painel. Sem ler o pedido que pausou, o
 * servidor manda só a classe — e é só ela que aparece. */
export function pausaParaOComponente(pausa: PausaDoPainel, stageKey: string): PausaDoStage {
  let motivo = pausa.classe
  if (pausa.pedido_id != null) motivo += ` no pedido #${pausa.pedido_id}`
  if (pausa.motivo) motivo += ` — ${pausa.motivo}`
  return {
    // O `PausaDaFila` não lê o id, e a resposta do painel não o traz.
    id: 0,
    stage_key: stageKey,
    motivo,
    task_request_id: pausa.chamado_id ?? null,
    // D-5: o painel não tem ação. Sem isto, nada de "Retomar a fila".
    pode_retomar: false,
  }
}

/** Recolhível no padrão do "ver saída" do laudo (`SaidaDaLeitura`, `DiagnosticoCard`). */
function Recolhivel({ id, titulo, children }: { id: string; titulo: string; children: ReactNode }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div>
      <Button
        variant="subtle"
        color="gray"
        size="compact-xs"
        px={0}
        leftSection={aberto ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        data-painel-toggle={id}
      >
        {titulo}
      </Button>
      {aberto && <Box pl="md" pt={4} data-painel-secao={id}>{children}</Box>}
    </div>
  )
}

function PedidoNoTopo({ pedido, temCartao, onIrAoCartao }: {
  pedido: CorpoDoPainel
  temCartao: boolean
  onIrAoCartao: (pedidoId: number) => void
}) {
  const vez = pedido.linha_do_tempo?.em_curso ?? null
  // "Ir ao cartão" é navegação: só quando a decisão é de quem lê e o cartão está nesta
  // conversa para onde rolar.
  const minhaVez = pedido.sou_o_pedinte && VEZ_DO_PEDINTE.includes(pedido.estado)
  return (
    <Stack gap={6} data-pedido-no-topo={pedido.id}>
      <Group gap={6} wrap="nowrap">
        <Dado>{`#${pedido.id}`}</Dado>
        <Badge size="xs" color="inovareTeal" variant="light">{rotuloDoEstado(pedido)}</Badge>
      </Group>
      {pedido.linha_do_tempo && (
        <LinhaDoTempoDoPedido linha={pedido.linha_do_tempo} mrUrl={pedido.mr_url} />
      )}
      {vez && (
        <Text size="sm" style={BARLOW}>
          A vez é de {vez.quem}
          {vez.prazo_ate && <> até <Dado>{prazo(vez.prazo_ate)}</Dado></>}.
        </Text>
      )}
      {pedido.aprovacao && (
        <Text size="xs" c="dimmed" style={BARLOW}>
          Falta a aprovação do eixo {pedido.aprovacao.eixo_pendente}
          {pedido.aprovacao.aprovadores.length > 0
            && `: ${pedido.aprovacao.aprovadores.map((a) => a.nome).join(', ')}`}.
        </Text>
      )}
      {pedido.janela?.aberta && (
        <Text size="xs" c="dimmed" style={BARLOW} data-janela-de-construcao>
          {pedido.janela.dispensou
            ? 'Janela de construção do stage: segue sem esperar o DevOps.'
            : 'Janela de construção aberta, mas este plan precisa de um Sênior'
              + (pedido.janela.sensiveis.length > 0 ? ` (${pedido.janela.sensiveis.join(', ')})` : '')
              + '.'}
        </Text>
      )}
      {minhaVez && temCartao && (
        <Group>
          <Button size="xs" variant="light" color="inovareTeal"
                  leftSection={<IconMessageCircle size={14} />}
                  onClick={() => onIrAoCartao(pedido.id)}>
            Ir ao cartão
          </Button>
        </Group>
      )}
    </Stack>
  )
}

function PlanResumido({ pedido }: { pedido: CorpoDoPainel }) {
  const acoes = Object.entries(pedido.acoes ?? {})
  const resumo = Object.entries(pedido.resumo ?? {})
    .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
  const inesperadas = pedido.inesperadas ?? []
  if (!pedido.linha_plan && acoes.length === 0 && resumo.length === 0 && inesperadas.length === 0) {
    return <Text size="xs" c="dimmed" style={BARLOW}>O plan deste pedido ainda não saiu.</Text>
  }
  return (
    <Stack gap={4}>
      {pedido.linha_plan && <Dado>{pedido.linha_plan}</Dado>}
      {acoes.map(([endereco, a]) => (
        <div key={endereco}><Dado>{`${a.join('/')} ${endereco}`}</Dado></div>
      ))}
      {resumo.length > 0 && (
        <Group gap={6}>
          {resumo.map(([chave, v]) => (
            <Text key={chave} size="xs" style={BARLOW}>{chave}: <Dado>{String(v)}</Dado></Text>
          ))}
        </Group>
      )}
      {inesperadas.length > 0 && (
        <Stack gap={2}>
          <Text size="xs" c="dimmed" style={BARLOW}>Mudanças fora do pedido:</Text>
          {inesperadas.map((endereco) => <div key={endereco}><Dado>{endereco}</Dado></div>)}
        </Stack>
      )}
    </Stack>
  )
}

function Recentes({ recentes }: { recentes: NonNullable<PainelDoStage['recentes']> }) {
  return (
    <Stack gap={8}>
      {recentes.map((r) => (
        <Stack key={r.id} gap={2} data-recente={r.id}>
          <Group gap={6} wrap="nowrap">
            <Dado>{`#${r.id}`}</Dado>
            <Badge size="xs" color="gray" variant="light">{r.estado}</Badge>
          </Group>
          {r.desfecho ? (
            <>
              <Text size="xs" style={BARLOW}>{r.desfecho.o_que_aconteceu}</Text>
              <Text size="xs" style={BARLOW}>Próximo passo: {r.desfecho.proximo_passo}</Text>
            </>
          ) : r.motivo_terminal && (
            <Text size="xs" style={BARLOW}>{r.motivo_terminal}</Text>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

/** §3.3: o diff do ajuste livre, lido só quando a seção abre (o `Recolhivel` só monta o
 *  filho aberto). O texto já sai mascarado do servidor; sem diff, só o `motivo` dele. */
function DiffDoPedido({ pedido, viaChamado }: { pedido: CorpoDoPainel; viaChamado: number | null }) {
  const { data, isLoading, isError } = useDiffDoPedido(pedido.id, viaChamado, pedido.tem_diff)
  if (isLoading) return <Text size="xs" c="dimmed" style={BARLOW}>Lendo o diff…</Text>
  if (isError || !data) {
    return <Text size="xs" c="dimmed" style={BARLOW}>Não deu para ler o diff deste pedido.</Text>
  }
  if (!data.disponivel) {
    return <Text size="xs" c="dimmed" style={BARLOW} data-diff-indisponivel>{data.motivo}</Text>
  }
  if (data.arquivos.length === 0) {
    return <Text size="xs" c="dimmed" style={BARLOW}>O pedido não muda arquivo nenhum.</Text>
  }
  return (
    <Stack gap={8}>
      {data.arquivos.map((a) => (
        <Stack key={a.arquivo} gap={4} data-diff-arquivo={a.arquivo}>
          <Group gap={6} wrap="nowrap">
            <Dado>{a.arquivo}</Dado>
            {a.novo && <Badge size="xs" color="inovareTeal" variant="light">arquivo novo</Badge>}
          </Group>
          <Code block>{a.diff}</Code>
          {a.cortado && (
            <Text size="xs" c="dimmed" style={BARLOW} data-diff-cortado>
              Cortado: o diff passou do limite de linhas e o resto não aparece aqui.
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

/** §3.6: cada item da lista, com o texto literal de quem pediu e em que pé está. */
function ItensDaLista({ itens }: { itens: ItemDaLista[] }) {
  return (
    <Stack gap={6}>
      {itens.map((item) => (
        <Group key={`${item.n}-${item.ref}`} gap={6} wrap="nowrap" align="flex-start"
               data-item-situacao={item.situacao}>
          {item.situacao === 'pedido' ? (
            <IconCheck size={14} color="var(--mantine-color-inovareTeal-6)" style={{ marginTop: 2 }} />
          ) : item.situacao === 'na_fila' ? (
            <IconClock size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
          ) : item.situacao === 'em_curso' ? (
            <IconPlayerPlay size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
          ) : (
            <IconAlertTriangle size={14} color="var(--mantine-color-yellow-6)" style={{ marginTop: 2 }} />
          )}
          <div>
            <Text size="xs" style={BARLOW}>{item.trecho}</Text>
            {item.situacao === 'pedido' ? (
              <Group gap={6} wrap="nowrap">
                <Dado>{item.pedido_id != null ? `pedido #${item.pedido_id}` : 'pedido'}</Dado>
                {item.rotulo && <Badge size="xs" color="inovareTeal" variant="light">{item.rotulo}</Badge>}
              </Group>
            ) : item.situacao === 'na_fila' ? (
              <Text size="xs" c="dimmed" style={BARLOW}>na fila (aguarda &quot;segue&quot;)</Text>
            ) : item.situacao === 'em_curso' ? (
              <Text size="xs" c="dimmed" style={BARLOW}>em curso</Text>
            ) : (
              <Text size="xs" c="dimmed" style={BARLOW}>
                {item.motivo ? `não saiu: ${item.motivo}` : 'não saiu'}
                {item.de_novo && ' · volta à fila'}
              </Text>
            )}
          </div>
        </Group>
      ))}
    </Stack>
  )
}

export interface PainelDoDevOpsProps {
  /** O pedido âncora: o stage é o dele, resolvido no servidor. */
  pedidoId: number
  viaChamado?: number | null
  /** Os pedidos com cartão nesta conversa: só eles ganham "Ir ao cartão". */
  pedidosComCartao: ReadonlySet<number>
  /** Rola a conversa até o cartão do pedido. Não muda estado nem chama API. */
  onIrAoCartao: (pedidoId: number) => void
}

export function PainelDoDevOps({ pedidoId, viaChamado = null, pedidosComCartao, onIrAoCartao }: PainelDoDevOpsProps) {
  const { data: painel, isLoading, isError } = usePainelDoStage(pedidoId, viaChamado)

  if (isLoading) {
    return <Text size="sm" c="dimmed" style={BARLOW}>Lendo o stage…</Text>
  }
  // `ancora: null`: o servidor não devolveu o pedido âncora — mesmo aviso da falha de
  // leitura, em vez de derrubar a tela ao ler `ancora.terminal`.
  if (isError || !painel || !painel.ancora) {
    return <Text size="sm" c="dimmed" style={BARLOW}>Não deu para ler o andamento deste stage.</Text>
  }

  const ancora = painel.ancora
  const emCurso = painel.em_curso
  const emCursoVisivel = emCurso && emCurso.visivel ? emCurso : null
  // Em cima: quem ocupa o stage. Com o stage livre, ou ocupado por um pedido que quem vê
  // não lê, a âncora ainda viva vem no lugar dela.
  const topo = emCursoVisivel ?? (!ancora.terminal ? ancora : null)
  // O pedido de que falam o plan e o andamento: o de cima, senão a âncora.
  const foco = topo ?? ancora
  const fila = [...(painel.fila ?? [])].sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0))
  const recentes = painel.recentes ?? []
  const checagens = ancora.conferencia?.checagens ?? []
  const itens = painel.itens_da_lista ?? []

  return (
    <Stack gap="sm" data-painel-do-devops>
      <Group gap={6}>
        <Text fw={600} size="sm" style={BARLOW}>Andamento do runner</Text>
        <Dado>{painel.stage_key}</Dado>
      </Group>

      <Stack gap={6} aria-label="Em curso">
        {emCurso && !emCurso.visivel && (
          <Text size="sm" style={BARLOW}>Em curso: {OPACO}.</Text>
        )}
        {topo ? (
          <PedidoNoTopo pedido={topo} temCartao={pedidosComCartao.has(topo.id)} onIrAoCartao={onIrAoCartao} />
        ) : !emCurso && (
          <Text size="sm" c="dimmed" style={BARLOW}>Nenhum pedido em curso neste stage.</Text>
        )}
      </Stack>

      {painel.pausa && <PausaDaFila pausa={pausaParaOComponente(painel.pausa, painel.stage_key)} />}

      {fila.length > 0 && (
        <Stack gap={4} aria-label="Fila do stage">
          <Text size="xs" fw={600} style={BARLOW}>Fila</Text>
          {fila.map((item, i) => {
            const posicao = item.posicao ?? i + 1
            return item.visivel ? (
              <Group key={`v-${item.id}`} gap={6} wrap="nowrap" data-fila-posicao={posicao}>
                <Dado>{`${posicao}º`}</Dado>
                <Dado>{`#${item.id}`}</Dado>
                <Text size="sm" style={BARLOW}>{rotuloDoEstado(item)}</Text>
              </Group>
            ) : (
              <Group key={`o-${posicao}`} gap={6} wrap="nowrap" data-fila-posicao={posicao}>
                <Dado>{`${posicao}º`}</Dado>
                <Text size="sm" c="dimmed" style={BARLOW}>{OPACO}</Text>
              </Group>
            )
          })}
        </Stack>
      )}

      {ancora.tem_diff && (
        <Recolhivel id="diff" titulo="Diff do arquivo">
          <DiffDoPedido pedido={ancora} viaChamado={viaChamado} />
        </Recolhivel>
      )}
      <Recolhivel id="plan" titulo="Plan resumido">
        <PlanResumido pedido={foco} />
      </Recolhivel>
      {foco.andamento_disponivel && (
        <Recolhivel id="andamento" titulo="Andamento ao vivo">
          <PainelDeExecucao pedidoId={foco.id} disponivel={foco.andamento_disponivel}
                            viaChamado={viaChamado} abertoDeInicio acoes={foco.acoes}
                            totalDeMudancas={totalDoPlan(foco.linha_plan)} />
        </Recolhivel>
      )}
      {checagens.length > 0 && (
        <Recolhivel id="conferencia" titulo="O que eu conferi / o que eu não verifiquei">
          <ChecagensVerificadas checagens={checagens} />
          <NaoVerificadas checagens={checagens} />
        </Recolhivel>
      )}
      {recentes.length > 0 && (
        <Recolhivel id="recentes" titulo="Recentes">
          <Recentes recentes={recentes} />
        </Recolhivel>
      )}
      {itens.length > 0 && (
        <Recolhivel id="itens" titulo="Itens do seu pedido">
          <ItensDaLista itens={itens} />
        </Recolhivel>
      )}
    </Stack>
  )
}
